import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import http, { type ApiRequestConfig } from "../httpService"
import type {
  ICodeFormatTemplateCreateOrUpdateDto,
  ICodeFormatTemplateDto,
  ICodeFormatTemplatePreviewDto,
  ICodeFormatTemplatePreviewOutputDto,
  INumberingModuleDto,
  IPagedFilterCodeFormatTemplateDto,
} from "./codeFormatTemplateDto"

/**
 * Mẫu mã tự sinh — `api/services/app/CodeFormatTemplate/*` (Feature-0371).
 *
 * Verb do ABP suy từ tiền tố tên method ở backend, không có attribute HTTP nào:
 * `GetAll`/`Get`/`GetListModule` → GET, `CreateOrUpdate` → POST, `Delete` → DELETE,
 * `Preview` → POST (tên không khớp tiền tố nào nên rơi về POST).
 */
class CodeFormatTemplateService {
  async getAll(
    params: IPagedFilterCodeFormatTemplateDto
  ): Promise<PagedResultDto<ICodeFormatTemplateDto>> {
    const res = await http.get("api/services/app/CodeFormatTemplate/GetAll", {
      params,
    })
    return res.data.result
  }

  async get(id: number): Promise<ICodeFormatTemplateDto> {
    const res = await http.get("api/services/app/CodeFormatTemplate/Get", {
      params: { id },
    })
    return res.data.result
  }

  async createOrUpdate(
    body: ICodeFormatTemplateCreateOrUpdateDto
  ): Promise<ICodeFormatTemplateDto> {
    const res = await http.post(
      "api/services/app/CodeFormatTemplate/CreateOrUpdate",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SAVING_SUCCESSFULLY"))
    return res.data.result
  }

  async delete(id: number): Promise<void> {
    await http.delete("api/services/app/CodeFormatTemplate/Delete", {
      params: { id },
    })
    notifySuccess(LNotification("SUCCESS"), LNotification("DELETE_SUCCESSFULLY"))
  }

  /** Danh sách nghiệp vụ cấu hình được mẫu — đã loại nhóm phiếu thu/chi ở backend. */
  async getListModule(): Promise<INumberingModuleDto[]> {
    const res = await http.get(
      "api/services/app/CodeFormatTemplate/GetListModule"
    )
    return res.data.result || []
  }

  /**
   * Render thử mẫu, KHÔNG tăng bộ đếm.
   *
   * `skipErrorNotification` vì hàm này chạy debounce theo từng phím gõ: mẫu dở dang (`{numb`) là lỗi
   * bình thường trong lúc soạn, để interceptor bắn toast thì mỗi ký tự lại một toast. Lỗi được trả
   * về cho caller hiển thị tại chỗ, ngay dưới ô nhập mẫu.
   */
  async preview(
    body: ICodeFormatTemplatePreviewDto
  ): Promise<ICodeFormatTemplatePreviewOutputDto> {
    const res = await http.post(
      "api/services/app/CodeFormatTemplate/Preview",
      body,
      { skipErrorNotification: true } as ApiRequestConfig
    )
    return res.data.result
  }
}

export default new CodeFormatTemplateService()
